import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MultiValue, SingleValue } from "react-select";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "../../../../components/input";
import Button from "../../../../components/button";
import {
  SummaryIssueIdentify,
  WeeklySummaryResponse,
} from "../../../../redux/features/weekly-summary-slice";
import {
  CommonStatus,
  getIssuesState,
  IssueResponse,
  issuesStateAction,
} from "../../../../redux/features/other-slice";
import { OptionValue } from "../../../../types/component";
import { isSelectSingleValue } from "../../../../core/utils";
import { AppDispatch } from "../../../../redux/store";
import { getUserState } from "../../../../redux/features/auth-slice";
import StyledSelect from "../../../../components/styled-select";
import { InfoOutlined } from "../../../../components/icons/info-outlined";
import SummaryPropertyDiscussDialog from "../summary-property-discuss-dialog";
import { TrashOutlined } from "../../../../components/icons/trash-outlined";
import { EditOutlined } from "../../../../components/icons/edit-outlined";
import { CheckOutlined } from "../../../../components/icons/check-outlined";

interface Props {
  summary: WeeklySummaryResponse | null;
  issues: SummaryIssueIdentify[];
  setIssues: (values: SummaryIssueIdentify[]) => void;
  onRefresh: () => void;
}

export default function IssueIdentifyForm(props: Props) {
  const { summary, issues, setIssues, onRefresh } = props;

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const issuesState = useSelector(getIssuesState);

  const [issueReasons, setIssueReasons] = useState<string[]>([]);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [editId, setEditId] = useState<string | null>(null); 
  const [editValues, setEditValues] = useState<SummaryIssueIdentify | null>(null); 
  const [selectedIssue, setSelectedIssue] =
    useState<SummaryIssueIdentify | null>(null);

    const getAvailableOptions = () => {
      const selectedTypes = issues.map((issue) => issue.type.id);
      const availableIssues = issuesState.data.filter(
        (issue: IssueResponse) => !selectedTypes.includes(issue.id)
      );
      return [
        {
          id: "null",
          title: "Nil",
          description: "Nothing",
          category: {
            id: "",
            title: "",
            status: CommonStatus.Inactive,
          },
        },
        ...availableIssues,
      ];
    };

  const fetchIssues = useCallback(async () => {
    if (user) {
      await dispatch(
        issuesStateAction({
          categoryId: user.category.id,
        })
      );
    }
  }, [dispatch, user]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  return (
    <>
      <div className="flex flex-col space-y-4">
        {issues.map((iss: SummaryIssueIdentify) => {
          const isEditing = editId === iss.type.id;
          return(
          <div
            key={iss.type.id}
            className="flex flex-col items-center space-y-4 p-4 bg-background-default border border-divider rounded-lg"
          >
            <div className="w-full flex flex-col space-y-4">
              <div>
                <label className="text-xs text-text-title">Type</label>
                {isEditing ? (
                  <div>
                    <StyledSelect
                      loading={issuesState.loading}
                      menuPosition="fixed"
                      options={getAvailableOptions().map(
                        (issue: IssueResponse) => ({
                          value: issue.id,
                          label: issue.title,
                        })
                      )}
                      isClearable={false}
                      value={
                        editValues &&
                        editValues.type.id &&
                        editValues.type.title
                          ? {
                              value: editValues.type.id,
                              label: editValues.type.title,
                            }
                          : undefined
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setSelectedReasons([]);
                          setEditValues({
                            ...editValues!,
                            type: {
                              id: value.value as string,
                              title: value.label as string,
                            },
                            reason:
                              (value.value as string) === "null" ? "--" : "",
                            action:
                              (value.value as string) === "null" ? "--" : "",
                          });
                        }
                      }}
                      exClassName="w-full"
                    />
                    {editValues?.type.id && (
                      <label className="flex items-center text-text-title text-xs mt-1">
                        <InfoOutlined className="h-4 w-4 mr-1" />
                        {editValues.type.id === "null"
                          ? "Nothing"
                          : issuesState.data.filter(
                              (e) => e.id === editValues.type.id
                            ).length > 0
                          ? issuesState.data.filter(
                              (e) => e.id === editValues.type.id
                            )[0].description
                          : ""}
                      </label>
                    )}
                  </div>
                ) : (
                  <Input type="text" value={iss.type.title} readonly />
                )}
              </div>
              {/* {user && user.team.id === dcTeam.id && (
                <div className="w-full flex flex-col space-y-2">
                  {iss.quantity !== null && (
                    <div>
                      <label className="text-xs text-text-title">
                        Quantity
                      </label>
                      <Input
                        type="text"
                        value={edit ? edit.quantity ?? "" : iss.quantity}
                        readonly={edit === null}
                        onChange={(event) => {
                          if (edit) {
                            setEdit({
                              ...edit,
                              quantity: event.target.value,
                            });
                          }
                        }}
                      />
                    </div>
                  )}
                  {iss.jobNumber !== null && (
                    <div>
                      <label className="text-xs text-text-title">
                        Job Number
                      </label>
                      <Input
                        type="text"
                        value={edit ? edit.jobNumber ?? "" : iss.jobNumber}
                        readonly={edit === null}
                        onChange={(event) => {
                          if (edit) {
                            setEdit({
                              ...edit,
                              jobNumber: event.target.value,
                            });
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              )} */}
              <div>
                <label className="text-xs text-text-title">Reason</label>
                {isEditing ? (
                  issueReasons.length > 0 ? (
                    <div>
                      <StyledSelect
                        loading={issuesState.loading}
                        menuPosition="fixed"
                        options={getAvailableOptions().map((issue: IssueResponse) => ({
                          value: issue.id,
                          label: issue.title,
                        }))}
                        isClearable={false}
                        value={
                          editValues &&
                            editValues.type.id &&
                            editValues.type.title
                            ? {
                              value: editValues.type.id,
                              label: editValues.type.title,
                            }
                            : undefined
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setSelectedReasons([]);
                            setEditValues({
                              ...editValues!,
                              type: {
                                id: value.value as string,
                                title: value.label as string,
                              },
                              reason: value.value === "null" ? "--" : "",
                              action: value.value === "null" ? "--" : "",
                            });
                          }
                        }}
                        exClassName="w-full"
                      />
                      {editValues?.reason === "" && (
                        <p className="text-error-main text-xs">
                          This Field is required
                        </p>
                      )}
                    </div>
                  ) : (
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      value={isEditing ? editValues?.reason : iss.reason}
                      readonly={isEditing ? editValues?.type.id === "null" : iss.type.id === "null"}
                      onChange={(event) => {
                        if (isEditing) {
                          setEditValues({
                            ...editValues!,
                            reason: event.target.value,
                          });
                        }
                      }}
                      errorText={
                        isEditing && editValues?.reason ===""
                            ? "This field is required"
                            : undefined
                      }
                  />
                  )
                ) : (
                  <Input
                    type="text"
                    inputType="textarea"
                    rows={3}
                    value={iss.reason}
                    readonly
                  />
                )}
              </div>
              <div>
                <label className="text-xs text-text-title">
                  Action / Proposed
                </label>
                <Input
                  type="text"
                  inputType="textarea"
                  rows={3}
                  value={isEditing ? editValues?.action : iss.action}
                  readonly={!isEditing}
                  onChange={(event) => {
                    if (isEditing) {
                      setEditValues({
                        ...editValues!,
                        action: event.target.value,
                      });
                    }
                  }}
                  errorText={
                    isEditing && editValues?.action === ""
                        ? "This field is required"
                        : undefined
                  }
                />
              </div>
            </div>
            <div className="w-full flex items-center justify-end space-x-2">
              {summary &&
                summary.issueIdentifies.findIndex(
                  (e) =>
                    e.type.id === iss.type.id &&
                    e.reason === iss.reason &&
                    e.quantity === iss.quantity &&
                    e.jobNumber === iss.jobNumber &&
                    e.action === iss.action
                ) !== -1 &&
                iss.type.id !== "null" && (
                  <Button
                    variant="outlined"
                    label={`Discusses (${iss.discuss.length})`}
                    onClick={() => setSelectedIssue(iss)}
                    exClassName="w-fit border-divider"
                  />
                )}
              <div className="flex flex-1 items-center justify-end space-x-2">
                <div
                  className="p-2 rounded cursor-pointer hover:bg-gray-200"
                  onClick={() => {
                    // if (edit === null) {
                    //   setEdit(iss);
                    //   setSelectedReasons(iss.reason.split(", "));
                    // } else
                     if (
                      editValues?.type.id &&
                      editValues.type.title &&
                      editValues.reason &&
                      editValues.action
                    ) {
                      const editIndex = issues.findIndex(
                        (e) =>
                          e.type.id === iss.type.id &&
                          e.quantity === iss.quantity &&
                          e.jobNumber === iss.jobNumber &&
                          e.reason === iss.reason &&
                          e.action === iss.action
                      );
                      if (editIndex !== -1) {
                        const tmp = [...issues];
                        tmp[editIndex] = {
                          ...tmp[editIndex],
                          type: editValues.type,
                          quantity: editValues.quantity,
                          jobNumber: editValues.jobNumber,
                          reason: editValues.reason,
                          action: editValues.action,
                        };
                        setIssues(tmp);
                        setEditValues(null);
                        setEditId(null);
                        setSelectedReasons([]);
                      }
                    } else {
                      // Enter edit mode
                      setEditId(iss.type.id);
                      setEditValues(iss);
                      setSelectedReasons(iss.reason.split(", "));
                    }
                  }}
                >
                  {isEditing ? (
                    <CheckOutlined className="h-4 w-4 text-success-main" />
                  ) : (
                    <EditOutlined className="h-4 w-4" />
                  )}
                </div>
                <div
                  className="p-2 rounded cursor-pointer hover:bg-gray-200"
                  onClick={() => {
                    const removedIndex = issues.findIndex(
                      (e) =>
                        e.type.id === iss.type.id &&
                        e.reason === iss.reason &&
                        e.quantity === iss.quantity &&
                        e.jobNumber === iss.jobNumber &&
                        e.action === iss.action
                    );
                    if (removedIndex !== -1) {
                      const tmp = issues.filter(
                        (_, index) => index !== removedIndex
                      );
                      setIssues(tmp);
                    }
                  }}
                >
                  <TrashOutlined className="h-4 w-4 text-error-main" />
                </div>
              </div>
            </div>
          </div>
        )})}
        {issues.findIndex((e) => e.type.id === "null") === -1 && (
          <Formik
            initialValues={{
              id: "",
              title: "",
              quantity: 0,
              jobNumber: "",
              reason: "",
              action: "",
            }}
            validationSchema={Yup.object().shape({
              id: Yup.string().required("This field is required"),
              title: Yup.string().required("This field is required"),
              reason: Yup.string().required("This field is required"),
              action: Yup.string().required("This field is required"),
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              setSubmitting(false);
              setIssues([
                ...issues,
                {
                  type: {
                    id: values.id,
                    title: values.title,
                  },
                  jobNumber: null,
                  quantity: null,
                  reason: values.reason,
                  action: values.action,
                  discuss: [],
                },
              ]);
              setSelectedReasons([]);
              resetForm();
            }}
          >
            {({ values, setValues, errors, handleChange, handleSubmit }) => (
              <Form
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
                className="flex flex-col items-center space-y-4 border border-divider rounded-lg p-4"
              >
                <div className="w-full flex flex-col space-y-4">
                  <div>
                    <label className="text-xs text-text-title">Type</label>
                    <div className="flex flex-col">
                      <StyledSelect
                        loading={issuesState.loading}
                        menuPosition="fixed"
                        options={getAvailableOptions().map(
                          (issue: IssueResponse) => ({
                            value: issue.id,
                            label: issue.title,
                          })
                        )}
                        isClearable={false}
                        value={
                          values.id !== "" && values.title !== ""
                            ? {
                              value: values.id,
                              label: values.title,
                            }
                            : { value: "", label: "Select..." }
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            const issueId = value.value as string;
                            setSelectedReasons([]);
                            setValues({
                              ...values,
                              id: issueId,
                              title: value.label as string,
                              reason:
                                (value.value as string) === "null" ? "--" : "",
                              action:
                                (value.value as string) === "null" ? "--" : "",
                            });
                          }
                        }}
                        exClassName="w-full"
                      />
                      {values.id && (
                        <label className="flex items-center text-text-title text-xs mt-1">
                          <InfoOutlined className="h-4 w-4 mr-1" />
                          {values.id === "null"
                            ? "Nothing"
                            : issuesState.data.filter((e) => e.id === values.id)
                                .length > 0
                            ? issuesState.data.filter(
                                (e) => e.id === values.id
                              )[0].description
                            : ""}
                        </label>
                      )}
                    </div>
                  </div>
                  {/* {user && user.team.id === dcTeam.id && (
                  <div className="w-full flex flex-col space-y-4">
                    <div>
                      <label className="text-xs text-text-title">
                        Quantity
                      </label>
                      <Input
                        type="number"
                        rows={3}
                        id="quantity"
                        value={values.quantity}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-text-title">
                        Job Number
                      </label>
                      <Input
                        type="text"
                        id="jobNumber"
                        value={values.jobNumber}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )} */}
                  <div>
                    <label className="text-xs text-text-title">Reason</label>
                    {issueReasons.length > 0 ? (
                      <div>
                        <StyledSelect
                          menuPosition="fixed"
                          options={getAvailableOptions().map(
                            (issue: IssueResponse) => ({
                              value: issue.id,
                              label: issue.title,
                            })
                          )}
                          value={{
                            value: "",
                            label:
                              selectedReasons.length > 0
                                ? selectedReasons.map((e) => e).join(", ")
                                : "Select...",
                          }}
                          isClearable={false}
                          onChange={(
                            value:
                              | SingleValue<OptionValue>
                              | MultiValue<OptionValue>
                          ) => {
                            if (value && isSelectSingleValue(value)) {
                              let tmpReasons = [];
                              if (
                                selectedReasons.findIndex(
                                  (e) => e === value.value
                                ) !== -1
                              ) {
                                tmpReasons = selectedReasons.filter(
                                  (r) => r !== value.value
                                );
                              } else {
                                tmpReasons = [
                                  ...selectedReasons,
                                  value.value as string,
                                ];
                              }
                              setSelectedReasons(tmpReasons);
                              setValues({
                                ...values,
                                reason: tmpReasons.join(", "),
                              });
                            }
                          }}
                          exClassName="w-full"
                          exOption={(value: OptionValue) => {
                            return {
                              background:
                                selectedReasons.findIndex(
                                  (e) => e === value.value
                                ) !== -1
                                  ? "rgb(148 163 184)"
                                  : undefined,
                              color:
                                selectedReasons.findIndex(
                                  (e) => e === value.value
                                ) !== -1
                                  ? "#ffffff"
                                  : undefined,
                            };
                          }}
                        />
                        {errors.reason && (
                          <p className="text-error-main text-xs">
                            {errors.reason}
                          </p>
                        )}
                      </div>
                    ) : (
                      <Input
                        type="text"
                        inputType="textarea"
                        rows={3}
                        id="reason"
                        value={values.reason}
                        onChange={handleChange}
                        errorText={errors.reason}
                        readonly={values.id === "null"}
                      />
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-text-title">
                      Action / Proposed
                    </label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      id="action"
                      value={values.action}
                      onChange={handleChange}
                      errorText={errors.action}
                      readonly={values.id === "null"}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  label="Add"
                  exClassName="w-fit self-end"
                  disabled={
                    Array.isArray(errors) ||
                    Object.values(errors).toString() != ""
                  }
                />
              </Form>
            )}
          </Formik>
        )}
      </div>
      {summary && (
        <SummaryPropertyDiscussDialog
          summaryId={summary.id}
          type="issue"
          property={selectedIssue}
          setProperty={setSelectedIssue}
          onSubmitted={onRefresh}
        />
      )}
    </>
  );
}
